import React, { useEffect, useState } from 'react';
import { Modal, Progress, Alert, Button, Card, Space, Tag, message } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, DownloadOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { BillExportProgressType } from '../../graphql/types/settlement/BillExportTypes';
import { downloadBillExportFile } from '../../utils/billExportDownload';
import { GET_EXPORT_PROGRESS, GET_EXPORT_FILE_DOWNLOAD_URL } from '../../graphql/queries/settlement/billExportQueries';

interface BillExportProgressModalProps {
  visible: boolean;
  taskId: string | null;  // 现在是逗号分隔的多个taskId
  onClose: () => void;
  onCompleted?: (tasks: BillExportProgressType[]) => void;
}

/**
 * 账单导出进度监控Modal
 * 支持同时显示多个导出任务的进度（Summary + Detail）
 */
const BillExportProgressModal: React.FC<BillExportProgressModalProps> = ({
  visible,
  taskId,
  onClose,
  onCompleted
}) => {
  // 解析任务ID列表
  const taskIds = taskId ? taskId.split(',').filter(id => id.trim()) : [];
  const [completedTasks, setCompletedTasks] = useState<BillExportProgressType[]>([]);

  // 调试日志
  useEffect(() => {
    console.log('BillExportProgressModal - taskId:', taskId);
    console.log('BillExportProgressModal - parsed taskIds:', taskIds);
  }, [taskId]);

  // 当弹窗打开时重置状态
  useEffect(() => {
    if (visible) {
      console.log('弹窗打开，重置完成状态');
      setCompletedTasks([]);
    }
  }, [visible]);

  // 为每个任务创建独立的查询（React Hook 规则：Hook 数量必须固定）
  // 我们最多支持5个任务（Summary + Detail 通常最多2个，5个已经足够）
  const MAX_TASKS = 5;

  // 创建懒查询实例（使用 no-cache 策略避免缓存问题）
  const [queryTask0, { data: data0 }] = useLazyQuery(GET_EXPORT_PROGRESS, { fetchPolicy: 'no-cache' });
  const [queryTask1, { data: data1 }] = useLazyQuery(GET_EXPORT_PROGRESS, { fetchPolicy: 'no-cache' });
  const [queryTask2, { data: data2 }] = useLazyQuery(GET_EXPORT_PROGRESS, { fetchPolicy: 'no-cache' });
  const [queryTask3, { data: data3 }] = useLazyQuery(GET_EXPORT_PROGRESS, { fetchPolicy: 'no-cache' });
  const [queryTask4, { data: data4 }] = useLazyQuery(GET_EXPORT_PROGRESS, { fetchPolicy: 'no-cache' });

  // 下载URL查询
  const [getExportFileDownloadUrl] = useLazyQuery(GET_EXPORT_FILE_DOWNLOAD_URL, { fetchPolicy: 'no-cache' });

  const queries = [queryTask0, queryTask1, queryTask2, queryTask3, queryTask4];
  const dataList = [data0, data1, data2, data3, data4];

  // 使用状态来缓存当前任务ID对应的进度数据
  const [currentTaskIds, setCurrentTaskIds] = useState<string>('');
  const [cachedProgresses, setCachedProgresses] = useState<BillExportProgressType[]>([]);

  // 当任务ID变化时，清除缓存
  useEffect(() => {
    const newTaskIdsKey = taskIds.join(',');
    if (newTaskIdsKey !== currentTaskIds) {
      console.log('任务ID变化，清除缓存:', currentTaskIds, '->', newTaskIdsKey);
      setCurrentTaskIds(newTaskIdsKey);
      setCachedProgresses([]);
    }
  }, [taskIds.join(',')]);

  // 收集所有任务进度（只收集当前任务ID的数据）
  const allProgresses: BillExportProgressType[] = dataList
    .slice(0, taskIds.length)
    .map((d, index) => {
      const progress = d?.getExportProgress;
      // 验证这个进度数据是否属于当前的任务ID
      if (progress && taskIds[index] && progress.taskId === taskIds[index]) {
        return progress;
      }
      return null;
    })
    .filter(Boolean) as BillExportProgressType[];

  // 检查是否所有任务都已完成
  const allCompleted = allProgresses.length === taskIds.length && 
    allProgresses.every(p => p.status === 'SUCCESS' || p.status === 'FAILED');

  // 定时轮询所有任务（完成后停止）
  useEffect(() => {
    if (!visible || taskIds.length === 0 || allCompleted) {
      console.log('停止轮询 - visible:', visible, 'taskIds:', taskIds.length, 'allCompleted:', allCompleted);
      return;
    }

    const fetchAllProgresses = () => {
      console.log('轮询任务进度...', taskIds);
      taskIds.forEach((id, index) => {
        if (index < MAX_TASKS) {
          queries[index]({ variables: { taskId: id } });
        }
      });
    };

    // 立即执行一次
    fetchAllProgresses();

    // 每5秒轮询一次
    const interval = setInterval(fetchAllProgresses, 5000);

    return () => {
      console.log('清除轮询定时器');
      clearInterval(interval);
    };
  }, [visible, taskIds.join(','), allCompleted]);

  useEffect(() => {
    if (allCompleted && completedTasks.length === 0) {
      setCompletedTasks(allProgresses);
      
      // 调用完成回调
      if (onCompleted && allProgresses.length > 0) {
        onCompleted(allProgresses);
      }
    }
  }, [allCompleted, allProgresses.length]);

  // 计算总体进度
  const totalProgress = allProgresses.length > 0
    ? Math.round(allProgresses.reduce((sum, p) => sum + p.progress, 0) / allProgresses.length)
    : 0;

  // 计算总体状态
  const getTotalStatus = () => {
    if (allProgresses.length === 0) return 'PENDING';
    if (allProgresses.some(p => p.status === 'FAILED')) return 'FAILED';
    if (allProgresses.every(p => p.status === 'SUCCESS')) return 'SUCCESS';
    return 'RUNNING';
  };

  const totalStatus = getTotalStatus();

  // 获取文件类型描述
  const getFileTypeLabel = (fileType: number) => {
    switch (fileType) {
      case 1:
        return '汇总';
      case 2:
        return '明细';
      default:
        return '未知';
    }
  };

  // 状态图标
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'FAILED':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'RUNNING':
      case 'PENDING':
        return <LoadingOutlined style={{ color: '#1890ff' }} />;
      default:
        return null;
    }
  };

  // 下载按钮点击
  const handleDownload = async (task: BillExportProgressType) => {
    if (!task.fileName) {
      message.error('文件名不存在');
      return;
    }

    try {
      message.loading('正在获取下载链接...', 0);
      const { data } = await getExportFileDownloadUrl({
        variables: { taskId: task.taskId }
      });
      message.destroy();

      const downloadUrl = data?.getExportFileDownloadUrl;
      if (downloadUrl) {
        downloadBillExportFile(downloadUrl, task.fileName);
      } else {
        message.error('获取下载链接失败');
      }
    } catch (error) {
      message.destroy();
      console.error('获取下载链接失败:', error);
      message.error('获取下载链接失败');
    }
  };

  return (
    <Modal
      title={`账单导出进度（${taskIds.length}个文件）`}
      open={visible}
      onCancel={onClose}
      footer={
        <Button onClick={onClose}>
          {totalStatus === 'RUNNING' ? '后台运行' : '关闭'}
        </Button>
      }
      width={700}
      maskClosable={false}
    >
      <div className="space-y-4">
        {/* 总体进度 */}
        <div>
          <div className="mb-2 font-medium">总体进度</div>
          <Progress
            percent={totalProgress}
            status={
              totalStatus === 'FAILED' ? 'exception' :
              totalStatus === 'SUCCESS' ? 'success' : 'active'
            }
          />
        </div>

        {/* 总体状态提示 */}
        {totalStatus === 'SUCCESS' && (
          <Alert
            message="所有文件导出完成"
            description="所有文件已生成，请分别下载或前往账单详情页面的「导出记录」中查看"
            type="success"
            showIcon
          />
        )}

        {totalStatus === 'FAILED' && (
          <Alert
            message="部分文件导出失败"
            description="请查看下方详情了解失败原因"
            type="error"
            showIcon
          />
        )}

        {/* 各个任务的详细进度 */}
        <div>
          <div className="mb-2 font-medium">文件详情</div>
          {allProgresses.length === 0 ? (
            <div className="text-center text-gray-500 py-4">
              正在查询任务进度... ({taskIds.length} 个任务)
            </div>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {allProgresses.map((task) => (
              <Card key={task.taskId} size="small" className="shadow-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="mr-3">
                      {getStatusIcon(task.status)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Tag color="blue">{getFileTypeLabel(task.fileType)}</Tag>
                        <span className="text-sm">
                          {task.status === 'PENDING' && '等待执行...'}
                          {task.status === 'RUNNING' && (task.currentStep || '正在导出...')}
                          {task.status === 'SUCCESS' && task.fileName}
                          {task.status === 'FAILED' && '导出失败'}
                        </span>
                      </div>
                      <Progress
                        percent={task.progress}
                        size="small"
                        status={
                          task.status === 'FAILED' ? 'exception' :
                          task.status === 'SUCCESS' ? 'success' : 'active'
                        }
                        showInfo={false}
                      />
                      {task.status === 'FAILED' && task.errorMessage && (
                        <div className="text-xs text-red-500 mt-1">{task.errorMessage}</div>
                      )}
                    </div>
                  </div>
                  {task.status === 'SUCCESS' && task.fileName && (
                    <Button
                      type="link"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(task)}
                    >
                      下载
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </Space>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BillExportProgressModal;
